'use client'

import SearchableSelect, {
    SearchableSelectOption,
} from '@/components/searchable-select'
import {
    CreateWhatsappTemplateData,
    UpdateWhatsappTemplateData,
    WHATSAPP_TEMPLATE_CATEGORY_LABELS,
    WHATSAPP_TEMPLATE_CONTENT_TYPE_LABELS,
    WhatsappTemplateCategory,
    WhatsappTemplateContentType,
    TemplateToken,
    createWhatsappTemplate,
    getWhatsappTemplate,
    getWhatsappTemplateTokens,
    updateWhatsappTemplate,
} from '@/lib/api/messaging/whatsapp-templates-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdArrowBack, MdSave } from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface FormState {
    name: string
    friendlyName: string
    category: WhatsappTemplateCategory
    contentType: WhatsappTemplateContentType
    language: string
    bodyText: string
    headerText: string
    footerText: string
    mediaUrl: string
    active: boolean
}

const EMPTY_FORM: FormState = {
    name: '',
    friendlyName: '',
    category: 'UTILITY',
    contentType: 'TEXT',
    language: 'pt_BR',
    bodyText: '',
    headerText: '',
    footerText: '',
    mediaUrl: '',
    active: true,
}

const CATEGORIES: WhatsappTemplateCategory[] = [
    'MARKETING',
    'UTILITY',
    'AUTHENTICATION',
]
const CONTENT_TYPES: WhatsappTemplateContentType[] = [
    'TEXT',
    'MEDIA',
    'QUICK_REPLY',
    'CALL_TO_ACTION',
    'CARD',
]

// WhatsApp/Meta exigem nome do template apenas em letras minúsculas, números
// e underscore (snake_case). Sanitiza o que o usuário digita para esse formato.
function sanitizeTemplateName(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // remove acentos
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_+/, '')
}

const TEMPLATE_NAME_REGEX = /^[a-z0-9_]+$/

const TEMPLATE_LANGUAGES: { value: string; label: string }[] = [
    { value: 'pt_BR', label: 'Português' },
    { value: 'en_US', label: 'Inglês' },
    { value: 'es_ES', label: 'Espanhol' },
]

const TOKEN_ENTITY_LABELS: Record<TemplateToken['entity'], string> = {
    client: 'Cliente',
    user: 'Usuário',
    company: 'Empresa',
    context: 'Data/hora',
}

export default function WhatsappTemplateFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const { canCreate, canUpdate } = useResourcePermissions(
        'messaging',
        'whatsapp_templates'
    )

    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [saving, setSaving] = useState(false)
    const [loadingTemplate, setLoadingTemplate] = useState(false)

    const [tokens, setTokens] = useState<TemplateToken[]>([])
    const [tokenPick, setTokenPick] = useState('')
    const bodyRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        getWhatsappTemplateTokens()
            .then(res => {
                if (res.success) setTokens(res.result)
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (!isEditing || !id) return
        setLoadingTemplate(true)
        getWhatsappTemplate(Number(id))
            .then(res => {
                if (res.success && res.result) {
                    const tpl = res.result
                    setForm({
                        name: tpl.name,
                        friendlyName: tpl.friendlyName,
                        category: tpl.category,
                        contentType: tpl.contentType,
                        language: tpl.language,
                        // Edita o corpo SEMÂNTICO quando existir; senão o legado.
                        bodyText: tpl.bodyTemplate || tpl.bodyText,
                        headerText: tpl.headerText ?? '',
                        footerText: tpl.footerText ?? '',
                        mediaUrl: tpl.mediaUrl ?? '',
                        active: tpl.active,
                    })
                } else {
                    toast.error(res.error || 'Template não encontrado')
                    navigate('/messaging/whatsapp-templates')
                }
            })
            .catch(() => {
                toast.error('Erro ao carregar template')
                navigate('/messaging/whatsapp-templates')
            })
            .finally(() => setLoadingTemplate(false))
    }, [id, isEditing, navigate])

    const tokenOptions: SearchableSelectOption[] = useMemo(
        () =>
            tokens.map(t => ({
                value: t.key,
                label: t.label,
                description: `{{${t.key}}} · ex: ${t.example}`,
                group: TOKEN_ENTITY_LABELS[t.entity],
            })),
        [tokens]
    )

    const tokenExamples = useMemo(
        () => new Map(tokens.map(t => [t.key, t.example])),
        [tokens]
    )

    // Pré-visualização: substitui {{token}} pelo exemplo do catálogo.
    const preview = useMemo(() => {
        return form.bodyText.replace(
            /\{\{\s*([a-zA-Z][a-zA-Z0-9_.]*)\s*\}\}/g,
            (full, key) => tokenExamples.get(key) ?? full
        )
    }, [form.bodyText, tokenExamples])

    const insertToken = (key: string) => {
        const snippet = `{{${key}}}`
        const el = bodyRef.current
        if (!el) {
            setForm(f => ({ ...f, bodyText: f.bodyText + snippet }))
            return
        }
        const start = el.selectionStart ?? el.value.length
        const end = el.selectionEnd ?? el.value.length
        const next =
            form.bodyText.slice(0, start) + snippet + form.bodyText.slice(end)
        setForm(f => ({ ...f, bodyText: next }))
        requestAnimationFrame(() => {
            el.focus()
            const pos = start + snippet.length
            el.setSelectionRange(pos, pos)
        })
    }

    const handleSubmit = async () => {
        if (
            !form.name.trim() ||
            !form.friendlyName.trim() ||
            !form.bodyText.trim()
        ) {
            toast.warn('Preencha nome interno, nome amigável e a mensagem')
            return
        }
        if (!isEditing && !TEMPLATE_NAME_REGEX.test(form.name.trim())) {
            toast.warn(
                'O nome interno deve conter apenas letras minúsculas, números e underscore (ex: boas_vindas_lead)'
            )
            return
        }
        // Regra da Meta: deve terminar com palavra estática, não com variável/pontuação.
        if (/\{\{[^}]+\}\}[^\p{L}\p{N}]*$/u.test(form.bodyText)) {
            toast.warn(
                'O texto deve terminar com uma palavra (texto fixo), não com uma variável ou pontuação. Finalize com uma saudação ou agradecimento.'
            )
            return
        }

        setSaving(true)
        try {
            if (!isEditing) {
                const payload: CreateWhatsappTemplateData = {
                    name: form.name.trim(),
                    friendlyName: form.friendlyName.trim(),
                    category: form.category,
                    contentType: form.contentType,
                    language: form.language || 'pt_BR',
                    bodyText: form.bodyText,
                    headerText: form.headerText || undefined,
                    footerText: form.footerText || undefined,
                    mediaUrl: form.mediaUrl || undefined,
                    active: form.active,
                }
                const res = await createWhatsappTemplate(payload)
                if (res.success) {
                    toast.success('Template criado')
                    navigate('/messaging/whatsapp-templates')
                } else {
                    toast.error(res.error || 'Erro ao criar template')
                }
            } else {
                const payload: UpdateWhatsappTemplateData = {
                    friendlyName: form.friendlyName.trim(),
                    category: form.category,
                    contentType: form.contentType,
                    language: form.language || 'pt_BR',
                    bodyText: form.bodyText,
                    headerText: form.headerText || undefined,
                    footerText: form.footerText || undefined,
                    mediaUrl: form.mediaUrl || undefined,
                    active: form.active,
                }
                const res = await updateWhatsappTemplate(Number(id), payload)
                if (res.success) {
                    toast.success('Template atualizado')
                    navigate('/messaging/whatsapp-templates')
                } else {
                    toast.error(res.error || 'Erro ao atualizar template')
                }
            }
        } finally {
            setSaving(false)
        }
    }

    const canSubmit = isEditing ? canUpdate : canCreate

    if (loadingTemplate) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <span className="loading loading-spinner loading-lg text-secondary" />
                <p className="text-base-content/60 text-sm">
                    Carregando template...
                </p>
            </div>
        )
    }

    const labelClass = 'text-sm font-medium text-base-content/80'

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => navigate('/messaging/whatsapp-templates')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <h1 className="text-2xl font-bold text-base-content">
                    {isEditing ? 'Editar template' : 'Novo template WhatsApp'}
                </h1>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Formulário */}
                <div className="xl:col-span-2 flex flex-col gap-6">
                    {/* Identificação */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body p-5 gap-4">
                            <h2 className="font-semibold text-base-content">
                                Identificação
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="flex flex-col gap-1">
                                    <span className={labelClass}>
                                        Nome interno
                                    </span>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm"
                                        value={form.name}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                name: sanitizeTemplateName(
                                                    e.target.value
                                                ),
                                            }))
                                        }
                                        disabled={isEditing}
                                        placeholder="boas_vindas_lead"
                                    />
                                    <span className="text-xs text-base-content/60">
                                        Apenas letras minúsculas, números e
                                        underscore (exigência do WhatsApp/Meta).
                                    </span>
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className={labelClass}>
                                        Nome amigável
                                    </span>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm"
                                        value={form.friendlyName}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                friendlyName: e.target.value,
                                            }))
                                        }
                                        placeholder="Boas-vindas Lead Frio"
                                    />
                                </label>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <label className="flex flex-col gap-1">
                                    <span className={labelClass}>Categoria</span>
                                    <select
                                        className="select select-bordered select-sm"
                                        value={form.category}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                category: e.target
                                                    .value as WhatsappTemplateCategory,
                                            }))
                                        }
                                    >
                                        {CATEGORIES.map(c => (
                                            <option key={c} value={c}>
                                                {
                                                    WHATSAPP_TEMPLATE_CATEGORY_LABELS[
                                                        c
                                                    ]
                                                }
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className={labelClass}>Idioma</span>
                                    <select
                                        className="select select-bordered select-sm"
                                        value={form.language}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                language: e.target.value,
                                            }))
                                        }
                                    >
                                        {TEMPLATE_LANGUAGES.map(lang => (
                                            <option
                                                key={lang.value}
                                                value={lang.value}
                                            >
                                                {lang.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className={labelClass}>
                                        Tipo de conteúdo
                                    </span>
                                    <select
                                        className="select select-bordered select-sm"
                                        value={form.contentType}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                contentType: e.target
                                                    .value as WhatsappTemplateContentType,
                                            }))
                                        }
                                    >
                                        {CONTENT_TYPES.map(c => (
                                            <option key={c} value={c}>
                                                {
                                                    WHATSAPP_TEMPLATE_CONTENT_TYPE_LABELS[
                                                        c
                                                    ]
                                                }
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Conteúdo */}
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body p-5 gap-4">
                            <h2 className="font-semibold text-base-content">
                                Conteúdo
                            </h2>
                            <label className="flex flex-col gap-1">
                                <span className={labelClass}>
                                    Cabeçalho (opcional)
                                </span>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    value={form.headerText}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            headerText: e.target.value,
                                        }))
                                    }
                                />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className={labelClass}>Mensagem</span>
                                <textarea
                                    ref={bodyRef}
                                    className="textarea textarea-bordered text-sm"
                                    rows={8}
                                    value={form.bodyText}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            bodyText: e.target.value,
                                        }))
                                    }
                                    placeholder="Digite a mensagem. Use as variáveis abaixo, ex: Olá {{client.name}}, tudo bem?"
                                />
                            </label>

                            {tokens.length > 0 && (
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs text-base-content/60">
                                        Inserir variável (preenchida
                                        automaticamente no envio):
                                    </span>
                                    <SearchableSelect
                                        options={tokenOptions}
                                        value={tokenPick}
                                        onChange={value => {
                                            if (value) {
                                                insertToken(value)
                                                setTokenPick('')
                                            }
                                        }}
                                        placeholder="Buscar variável (ex: nome do cliente)..."
                                        searchPlaceholder="Buscar variável..."
                                    />
                                </div>
                            )}

                            <label className="flex flex-col gap-1">
                                <span className={labelClass}>
                                    Rodapé (opcional)
                                </span>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm"
                                    value={form.footerText}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            footerText: e.target.value,
                                        }))
                                    }
                                />
                            </label>

                            {form.contentType === 'MEDIA' && (
                                <label className="flex flex-col gap-1">
                                    <span className={labelClass}>
                                        URL da mídia
                                    </span>
                                    <input
                                        type="url"
                                        className="input input-bordered input-sm"
                                        value={form.mediaUrl}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                mediaUrl: e.target.value,
                                            }))
                                        }
                                    />
                                </label>
                            )}

                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-sm toggle-secondary"
                                    checked={form.active}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            active: e.target.checked,
                                        }))
                                    }
                                />
                                <span className="label-text">
                                    Template ativo
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost flex-1"
                            onClick={() =>
                                navigate('/messaging/whatsapp-templates')
                            }
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary flex-1 gap-2"
                            onClick={handleSubmit}
                            disabled={saving || !canSubmit}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdSave className="text-base" />
                            )}
                            {isEditing ? 'Salvar' : 'Criar template'}
                        </button>
                    </div>
                </div>

                {/* Pré-visualização */}
                <div className="xl:col-span-1">
                    <div className="card bg-base-100 shadow-sm xl:sticky xl:top-4">
                        <div className="card-body p-5 gap-3">
                            <h2 className="font-semibold text-base-content">
                                Pré-visualização
                            </h2>
                            <div className="rounded-xl bg-[#e5ddd5] p-4 min-h-[160px]">
                                <div className="bg-white rounded-lg shadow-sm p-3 text-sm text-gray-800 whitespace-pre-wrap break-words">
                                    {form.headerText && (
                                        <div className="font-bold mb-1">
                                            {form.headerText}
                                        </div>
                                    )}
                                    <div>
                                        {preview || (
                                            <span className="text-gray-400">
                                                A mensagem aparecerá aqui...
                                            </span>
                                        )}
                                    </div>
                                    {form.footerText && (
                                        <div className="text-xs text-gray-500 mt-2">
                                            {form.footerText}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <span className="text-xs text-base-content/50">
                                As variáveis são mostradas com valores de exemplo;
                                no envio são preenchidas com os dados reais.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
